import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { OrganizationState } from '@state/organization.state';
import { userRoles } from 'app/@types/dto/userRoles.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';
import { ConfirmationService } from 'primeng/api';
import { EMPTY, Observable, combineLatest } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { SecurityRoleDto } from 'app/@types/dto/security-role-dto.model';
import { RoleHttpService } from '@services/datasources/role-http.service';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { PostHogService } from '@services/common/post-hog.service';

/**
 * One of the three roles an organization owner may assign to a member, keyed to
 * its backend role GUID (DbConstants.AssignableOrgRoles).
 * - Main user: full access to every entity of the organization.
 * - Editor: read + write; can be restricted to a subset of entities.
 * - Viewer: read-only; can be restricted to a subset of entities.
 */
interface AssignableRoleOption {
    id: string;
    value: string;
    labelKey: string;
}

interface EditUserFormControls {
    lastname: FormControl<string | null>;
    firstname: FormControl<string | null>;
    email: FormControl<string | null>;
    phone_number: FormControl<string | null>;
    roleId: FormControl<string | null>;
    entities: FormControl<OrganizationEntityDto[] | null>;
}

interface NewUserRolePayload {
    role_uid: string;
    role_name: string;
    org_entity_uid?: string | null;
    organization_uid: string;
}

/**
 * Payload shape for the `addUserToScan` backend endpoint. The backend
 * (UserController.AddUserToExistingScan → NormalizeInviteRole) validates the
 * requested role and applies its entity scope, so the chosen role_uid is sent
 * explicitly per role row. The generated SecurityRoleDto marks more fields as
 * required than the create-user contract needs, hence this dedicated shape.
 */
interface NewUserPayload {
    email: string;
    phone_number: string;
    locale: string;
    firstname?: string;
    lastname?: string;
    roles: NewUserRolePayload[];
}

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class EditUserComponent implements OnInit {
    @Output() closeEditUser = new EventEmitter<{ translateKey: string; type: string } | undefined>();
    @Select(UserState.getCurrentUserClaims) userClaim$!: Observable<UserClaims>;

    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    entitiesOptions: OrganizationEntityDto[] = [];
    userForm: FormGroup<EditUserFormControls>;
    // In-flight guard for the submit request: drives the button spinner and blocks double-submit.
    public saving = false;

    // Role GUIDs mirror DbConstants (main_user / secondary_user / viewer).
    readonly mainUserRoleId = 'c1eee594-6453-4b95-bd80-541348fc2f6b';
    readonly editorRoleId = '43f2cdf8-8a25-4bc9-94cd-6314958f06ad';
    readonly viewerRoleId = 'f5ac0e13-8a26-48ac-a6bd-8c88b82aab76';
    readonly roleOptions: AssignableRoleOption[] = [
        { id: this.mainUserRoleId, value: userRoles.main_user, labelKey: 'settings.users.roles.mainUser' },
        { id: this.editorRoleId, value: userRoles.secondary_user, labelKey: 'settings.users.roles.editor' },
        { id: this.viewerRoleId, value: userRoles.viewer, labelKey: 'settings.users.roles.viewer' },
    ];
    // Default for a new invite / unspecified selection. Bound to the named id, not an array
    // position, so reordering roleOptions can never silently change the default.
    private readonly defaultRoleId = this.editorRoleId;

    @Input() user?: UserInfoDto;
    @Input() orgId!: string;
    @Input() isOrgOwner: boolean = false;
    // True when the edited user is the organization's only one.
    @Input() isLastUser: boolean = false;
    // True when the edited user is the organization's only Main user: their role is locked.
    @Input() isLastMainUser: boolean = false;
    constructor(
        private readonly translateService: CustomTranslateService,
        private readonly confirmationService: ConfirmationService,
        private readonly userService: UserHttpService,
        private readonly roleService: RoleHttpService,
        private readonly postHogService: PostHogService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.userForm = new FormGroup<EditUserFormControls>({
            lastname: new FormControl<string | null>('', { validators: [Validators.required] }),
            firstname: new FormControl<string | null>('', { validators: [Validators.required] }),
            email: new FormControl<string | null>('', {
                validators: [Validators.required, CustomValidators.emailValidator],
            }),
            phone_number: new FormControl<string | null>('', {
                validators: [CustomValidators.optionalPhoneNumberValidator],
            }),
            roleId: new FormControl<string | null>(this.defaultRoleId),
            entities: new FormControl<OrganizationEntityDto[] | null>(null),
        });
    }

    ngOnInit(): void {
        combineLatest([this.entities$, this.userClaim$]).subscribe(([entities]) => {
            this.entitiesOptions = entities.filter((entity) => !entity.deleted);
            this.initValue();
            if (this.user?.uid) {
                // Existing user: identity fields stay read-only; owners may change the
                // role and the entity scope.
                this.userForm.disable();
                if (this.isOrgOwner) {
                    if (!this.isLastMainUser) {
                        this.userForm.get('roleId')?.enable();
                    }
                    this.userForm.get('entities')?.enable();
                }
            }
        });
    }

    /** Main user always spans every entity, so entity scoping is hidden for it. */
    get isMainUserSelected(): boolean {
        return this.userForm.controls.roleId.value === this.mainUserRoleId;
    }

    /** True when the user being edited currently holds the Main user role for this org. */
    get editedUserIsMainUser(): boolean {
        return !!this.user?.roles?.some((r) => r.organization_uid === this.orgId && r.role_uid === this.mainUserRoleId);
    }

    /** Only an owner may remove a user, and never a Main user nor the organization's only user. */
    get canRemoveUser(): boolean {
        return !!this.user?.uid && this.isOrgOwner && !this.editedUserIsMainUser && !this.isLastUser;
    }

    get selectedRoleLabelKey(): string {
        return this.roleOptions.find((o) => o.id === this.userForm.controls.roleId.value)?.labelKey ?? '';
    }

    close() {
        this.closeEditUser.emit();
    }

    initValue() {
        if (!this.user) {
            this.userForm.patchValue({ roleId: this.defaultRoleId });
            return;
        }
        this.userForm.patchValue(this.user);
        const role = this.user.roles.find((elt) => elt.organization_uid === this.orgId);
        this.userForm.patchValue({ roleId: role?.role_uid ?? this.defaultRoleId });
        if (this.user.roles.length > 0) {
            const userEntities = this.user.roles.map((r) => r.org_entity_uid);
            const entities = this.entitiesOptions
                .filter((entity): entity is OrganizationEntityDto & { id: string } => entity.id !== undefined)
                .filter((entity) => userEntities.filter((elt) => elt !== undefined).includes(entity.id));

            this.userForm.patchValue({ entities });
        }
    }

    onDelete(): void {
        this.confirmationService.confirm(
            destructiveConfirmation({
                message: this.translateService.instant('common.confirm_message'),
                header: this.translateService.instant('common.confirm_title'),
                acceptLabel: this.translateService.instant('common.confirm_button'),
                rejectLabel: this.translateService.instant('common.cancel_button'),
                icon: 'ms ms-warning',
                accept: () => {
                    if (this.orgId && this.user?.uid) {
                        // On failure the error interceptor already shows why; stay on the form.
                        this.userService
                            .removeUser(this.orgId, this.user.uid)
                            .pipe(catchError(() => EMPTY))
                            .subscribe(() =>
                                this.closeEditUser.emit({
                                    translateKey: 'settings.users.toast.success.remove',
                                    type: 'success',
                                }),
                            );
                    }
                },
                reject: () => {
                    this.closeEditUser.emit();
                },
            }),
        );
    }

    save() {
        if (this.saving || !revealFormErrors(this.userForm, this.host.nativeElement)) {
            return;
        }
        const formValue = this.userForm.value;
        const roleId = formValue.roleId ?? this.defaultRoleId;
        const roleName = this.roleOptions.find((o) => o.id === roleId)?.value ?? userRoles.secondary_user;
        // Main user spans all entities; Editor/Viewer are scoped to the selected
        // entities (an empty selection means "all entities" server-side).
        const scopedEntities = this.isMainUserSelected ? [] : (formValue.entities ?? []);

        if (this.user?.uid) {
            const data: SecurityRoleDto[] =
                scopedEntities.length > 0
                    ? scopedEntities.map((entity) => ({
                          role_uid: roleId,
                          role_name: roleName,
                          user_uid: this.user?.uid,
                          organization_uid: this.orgId,
                          org_entity_uid: entity.id,
                      }))
                    : [
                          {
                              role_uid: roleId,
                              role_name: roleName,
                              user_uid: this.user?.uid,
                              organization_uid: this.orgId,
                              org_entity_uid: undefined,
                          },
                      ];
            this.saving = true;
            // On failure the error interceptor already shows why; stay on the form.
            this.roleService
                .updateEntity(this.orgId, data)
                .pipe(
                    catchError(() => EMPTY),
                    finalize(() => (this.saving = false)),
                )
                .subscribe(() => {
                    this.postHogService.capture('user_invited', { is_new: false });
                    this.closeEditUser.emit({
                        translateKey: 'settings.users.toast.success.update',
                        type: 'success',
                    });
                });
            return;
        }

        const userData: NewUserPayload = {
            email: formValue.email ?? '',
            phone_number: CustomValidators.toE164Phone(formValue.phone_number),
            locale: 'en',
            firstname: formValue.firstname ?? undefined,
            lastname: formValue.lastname ?? undefined,
            roles:
                scopedEntities.length > 0
                    ? scopedEntities.map((entity) => ({
                          role_uid: roleId,
                          role_name: roleName,
                          org_entity_uid: entity.id,
                          organization_uid: entity.organizationId,
                      }))
                    : [{ role_uid: roleId, role_name: roleName, org_entity_uid: null, organization_uid: this.orgId }],
        };

        this.saving = true;
        // Cast is deliberate: NewUserPayload is the addUserToScan contract (see NewUserPayload doc).
        // On failure (e.g. an admin account's email) the error interceptor already shows why; the
        // form stays open so the email can be corrected.
        this.userService
            .addUserToScan(userData as unknown as UserInfoDto)
            .pipe(
                catchError(() => EMPTY),
                finalize(() => (this.saving = false)),
            )
            .subscribe(() => {
                this.postHogService.capture('user_invited', { is_new: true });
                this.closeEditUser.emit({ translateKey: 'settings.users.toast.success.add', type: 'success' });
            });
    }
}
